/** Single-message mailbox changes. No permanent delete, arbitrary folder or batch write. */
export const EMAIL_OPERATIONS = ["archive", "read", "unread", "star", "unstar", "trash"] as const;
export type EmailOperation = typeof EMAIL_OPERATIONS[number];
export type EmailProvider = "gmail" | "outlook_mail";
export interface EmailOrganizationSnapshot {
  provider: EmailProvider;
  id: string;
  version: string;
  labels?: string[];
  folder_id?: string;
  is_read?: boolean;
  flag?: "notFlagged" | "flagged" | "complete";
}
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
export const mailId = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 500 && !/[\u0000-\u0020]/.test(v);
const version = (v: unknown): v is string => typeof v === "string" && v.length > 0 && v.length <= 1000 && !/[\u0000-\u001f]/.test(v);
export function organizationSnapshot(provider: EmailProvider, raw: unknown): EmailOrganizationSnapshot | null {
  if (!object(raw) || !mailId(raw.id)) return null;
  if (provider === "gmail") {
    if (!version(raw.historyId) || !Array.isArray(raw.labelIds) || raw.labelIds.length > 200 ||
        !raw.labelIds.every(mailId)) return null;
    return { provider, id: raw.id, version: raw.historyId, labels: [...new Set(raw.labelIds as string[])].sort() };
  }
  if (!version(raw.changeKey) || !mailId(raw.parentFolderId) || typeof raw.isRead !== "boolean" ||
      !object(raw.flag) || !["notFlagged", "flagged", "complete"].includes(String(raw.flag.flagStatus))) return null;
  return { provider, id: raw.id, version: raw.changeKey, folder_id: raw.parentFolderId,
    is_read: raw.isRead, flag: raw.flag.flagStatus as EmailOrganizationSnapshot["flag"] };
}
export function parseOrganizationSnapshot(raw: unknown, provider: EmailProvider, messageId: string): EmailOrganizationSnapshot {
  if (!object(raw) || raw.provider !== provider || raw.id !== messageId ||
      Object.keys(raw).some(k => !(provider === "gmail" ? ["provider","id","version","labels"] : ["provider","id","version","folder_id","is_read","flag"]).includes(k))) throw new Error("invalid_organization_snapshot");
  const snapshot = organizationSnapshot(provider, provider === "gmail"
    ? { id: raw.id, historyId: raw.version, labelIds: raw.labels }
    : { id: raw.id, changeKey: raw.version, parentFolderId: raw.folder_id, isRead: raw.is_read, flag: { flagStatus: raw.flag } });
  if (!snapshot) throw new Error("invalid_organization_snapshot");
  return snapshot;
}
export const sameOrganizationSnapshot = (a: EmailOrganizationSnapshot, b: EmailOrganizationSnapshot) =>
  JSON.stringify(a) === JSON.stringify(b);
export function parseOrganizationRequest(body: Record<string, unknown>) {
  if (!["gmail", "outlook_mail"].includes(String(body.provider)) || !mailId(body.message_id) ||
      !EMAIL_OPERATIONS.includes(body.operation as EmailOperation)) throw new Error("invalid_organization_request");
  const provider = body.provider as EmailProvider, messageId = body.message_id;
  return { provider, messageId, operation: body.operation as EmailOperation,
    snapshot: parseOrganizationSnapshot(body.expected_snapshot, provider, messageId) };
}
export function hasEmailOrganizationProof(body: Record<string, unknown>, raw: unknown): boolean {
  try {
    const request = parseOrganizationRequest(body);
    if (!object(raw) || raw.operation !== request.operation || raw.message_id !== request.messageId ||
        raw.evidence !== "provider_readback" || !mailId(raw.id) || raw.external_id !== raw.id ||
        typeof raw.request_hash !== "string" || !/^[0-9a-f]{64}$/.test(raw.request_hash)) return false;
    const before = parseOrganizationSnapshot(raw.before, request.provider, request.messageId);
    const after = parseOrganizationSnapshot(raw.after, request.provider, raw.id);
    if (!sameOrganizationSnapshot(before, request.snapshot)) return false;
    if (request.provider === "gmail") {
      if (after.id !== request.messageId) return false;
      const labels = after.labels!;
      if (request.operation === "archive") return !labels.includes("INBOX");
      if (request.operation === "read") return !labels.includes("UNREAD");
      if (request.operation === "unread") return labels.includes("UNREAD");
      if (request.operation === "star") return labels.includes("STARRED");
      if (request.operation === "unstar") return !labels.includes("STARRED");
      return labels.includes("TRASH");
    }
    if (["archive","trash"].includes(request.operation)) return mailId(raw.destination_id) && after.folder_id === raw.destination_id;
    if (after.id !== request.messageId || after.folder_id !== before.folder_id) return false;
    if (request.operation === "read" || request.operation === "unread") return after.is_read === (request.operation === "read");
    return after.flag === (request.operation === "star" ? "flagged" : "notFlagged");
  } catch { return false; }
}
export async function organizeMail(input: {
  provider: EmailProvider; messageId: string; operation: EmailOperation; snapshot: EmailOrganizationSnapshot;
  headers: Record<string, string>; read: (url: string, headers?: Record<string, string>) => Promise<Record<string, unknown>>;
  write: (url: string, init: RequestInit) => Promise<Record<string, unknown>>; assertCurrent: () => Promise<void>;
}) {
  const base = input.provider === "gmail" ? "https://gmail.googleapis.com/gmail/v1/users/me" : "https://graph.microsoft.com/v1.0/me";
  const messageUrl = base + "/messages/" + encodeURIComponent(input.messageId);
  const readUrl = (url: string) => input.provider === "gmail" ? url + "?format=minimal" : url + "?$select=id,changeKey,parentFolderId,isRead,flag";
  let destinationId: string | undefined;
  if (input.provider === "outlook_mail" && ["archive","trash"].includes(input.operation)) {
    const folder = await input.read(base + "/mailFolders/" + (input.operation === "archive" ? "archive" : "deleteditems") + "?$select=id", input.headers);
    if (!mailId(folder.id)) throw new Error("organization_destination_unavailable");
    destinationId = folder.id;
  }
  const before = organizationSnapshot(input.provider, await input.read(readUrl(messageUrl), input.headers));
  if (!before || !sameOrganizationSnapshot(before, input.snapshot)) throw new Error("email_changed");
  await input.assertCurrent();
  let resultId = input.messageId;
  if (input.provider === "gmail") {
    const add: string[] = [], remove: string[] = [];
    if (input.operation === "archive") remove.push("INBOX");
    if (input.operation === "read") remove.push("UNREAD");
    if (input.operation === "unread") add.push("UNREAD");
    if (input.operation === "star") add.push("STARRED");
    if (input.operation === "unstar") remove.push("STARRED");
    await input.write(messageUrl + (input.operation === "trash" ? "/trash" : "/modify"), {
      method: "POST", headers: input.headers,
      body: JSON.stringify(input.operation === "trash" ? {} : { addLabelIds: add, removeLabelIds: remove }),
    });
  } else if (destinationId) {
    const moved = await input.write(messageUrl + "/move", { method: "POST", headers: input.headers, body: JSON.stringify({ destinationId }) });
    if (!mailId(moved.id)) throw new Error("organization_readback_failed");
    resultId = moved.id;
  } else {
    const patch = ["read","unread"].includes(input.operation) ? { isRead: input.operation === "read" }
      : { flag: { flagStatus: input.operation === "star" ? "flagged" : "notFlagged" } };
    await input.write(messageUrl, { method: "PATCH", headers: input.headers, body: JSON.stringify(patch) });
  }
  const after = organizationSnapshot(input.provider, await input.read(readUrl(base + "/messages/" + encodeURIComponent(resultId)), input.headers));
  if (!after) throw new Error("organization_readback_failed");
  const result = { id: resultId, external_id: resultId, message_id: input.messageId,
    operation: input.operation, before, after, ...(destinationId ? { destination_id: destinationId } : {}), evidence: "provider_readback" };
  if (!hasEmailOrganizationProof({ provider: input.provider, message_id: input.messageId, operation: input.operation, expected_snapshot: input.snapshot }, { ...result, request_hash: "0".repeat(64) })) throw new Error("organization_readback_failed");
  return result;
}
